import type {
  FieldDefinition,
  FieldType,
  Locale,
  MessageKey,
  MessageParams,
} from '@sentra/contracts';
import {
  checkCrossFieldRules,
  fieldValueSchema,
  isMessageKey,
  MAX_ADHOC_FIELDS_PER_PERSON,
  messageKeyOfIssue,
  paramsOfIssue,
  translate,
} from '@sentra/contracts';

/**
 * The registration form's values, as data rather than as component state.
 *
 * **Everything here is a pure function over plain records**, for the reason
 * `features/conflicts/conflict.ts` gives: the rules this enforces are safety properties — a value
 * the form accepted is never one the API refuses, a blank optional field is OMITTED rather than
 * submitted as an empty string, an invalid value never reaches a request — and a property enforced
 * only inside a component is a property nobody can test without a browser.
 *
 * **The validators are the SERVER'S validators.** `fieldValueSchema` and `checkCrossFieldRules` come
 * from `@sentra/contracts` and are the same functions `packages/server/src/persons` compiles its
 * request validator from. That is the whole point of the contracts package: a second implementation
 * of "is this a valid passport number" in the browser would differ from the server's the day either
 * changed, and the difference would surface as a refusal an operator cannot act on, at a desk, with
 * somebody standing in front of them.
 *
 * **A blank is omitted, never sent.** `personFieldsSchema` makes every non-required field
 * `.nullish().optional()`, so an empty string submitted for an optional text field is not "no
 * value": it is a value that fails nothing and stores an empty ciphertext, and the completeness
 * warning would then never mention the field again. Omission is what makes the warning true.
 */

/** What a control holds. A number is held as the string the input shows and coerced on submit. */
export type FormValue = string | boolean | readonly string[];

export type FormValues = Readonly<Record<string, FormValue>>;

/**
 * The empty state of every control, keyed by type.
 *
 * A table rather than a chain of conditions, so "what does an empty X hold" is answerable by
 * reading one line, and so the answer for a type added to the contract is a missing key the
 * compiler names rather than a silent fall-through to the empty string.
 *
 * The empty multiselect is frozen and shared: every write in this module builds a new array, so one
 * instance is safe, and freezing it is what makes that a property rather than a habit.
 */
const NO_SELECTION: readonly string[] = Object.freeze([]);

const BLANK_VALUES: Readonly<Record<FieldType, FormValue>> = {
  text: '',
  textarea: '',
  number: '',
  date: '',
  boolean: false,
  select: '',
  multiselect: NO_SELECTION,
  phone: '',
  email: '',
  country: '',
  passport: '',
};

/** The empty state of one field's control, decided by its type rather than by a shared default. */
export function blankValueFor(definition: Pick<FieldDefinition, 'type'>): FormValue {
  return BLANK_VALUES[definition.type];
}

/**
 * Whether a control holds nothing.
 *
 * `false` is NOT blank: a checkbox always holds an answer, and treating "no" as missing would put
 * every unticked box into the completeness warning forever.
 */
export function isBlank(value: FormValue): boolean {
  if (typeof value === 'boolean') {
    return false;
  }
  return typeof value === 'string' ? value.trim().length === 0 : value.length === 0;
}

/** Every active field's blank value, which is what an empty registration form starts as. */
export function blankValues(definitions: readonly FieldDefinition[]): FormValues {
  const values: Record<string, FormValue> = {};
  for (const definition of activeFields(definitions)) {
    values[definition.key] = blankValueFor(definition);
  }
  return values;
}

/**
 * The active definitions, in the order an administrator arranged them.
 *
 * Retired fields are filtered out rather than rendered disabled: `personFieldsSchema` refuses a
 * value for one on a create, so a control that could be typed into would be a control whose contents
 * are refused by the server with a message about an unknown field.
 *
 * Module-private since the form started grouping: every caller outside this file wants
 * {@link groupedFields}, which is this list partitioned, and a second door onto the flat one is a
 * second answer to "what order is this form in".
 */
function activeFields(definitions: readonly FieldDefinition[]): readonly FieldDefinition[] {
  return definitions
    .filter((definition) => definition.status === 'active')
    .toSorted((left, right) =>
      left.displayOrder === right.displayOrder
        ? left.key.localeCompare(right.key, 'en')
        : left.displayOrder - right.displayOrder,
    );
}

/** One group of the form: the section its fields declare, and the fields themselves. */
export interface FieldGroup {
  /** The stored section string, or `null` for fields that declare none. */
  readonly section: string | null;
  readonly fields: readonly FieldDefinition[];
}

/**
 * The fields, partitioned into the sections they declare, in the order the form renders them.
 *
 * **Grouping follows `displayOrder` and adds no ordering of its own**, which is what keeps one
 * answer to "what order is this form in". A section appears where its FIRST field appears and
 * collects every field that names it, so an administrator who interleaves two sections gets a
 * deterministic layout rather than a group that opens and closes twice — and one who reorders the
 * fields reorders the groups with them.
 *
 * `null` is a real group rather than a fallback: a field an administrator left unsectioned has to
 * render somewhere, and dropping it into whichever group happened to be open would put it under a
 * heading that is not about it.
 */
export function groupedFields(definitions: readonly FieldDefinition[]): readonly FieldGroup[] {
  return groupBySection(activeFields(definitions));
}

/**
 * The same partition over an arbitrary list, for the record screen.
 *
 * The record shows a RETIRED definition when the record still holds a value for it, so it cannot go
 * through `activeFields`. It hands in its own list, already in the order the server returned.
 */
export function groupBySection(fields: readonly FieldDefinition[]): readonly FieldGroup[] {
  const groups: { section: string | null; fields: FieldDefinition[] }[] = [];
  const bySection = new Map<string | null, FieldDefinition[]>();

  for (const definition of fields) {
    const section = definition.section;
    const existing = bySection.get(section);
    if (existing === undefined) {
      const created: FieldDefinition[] = [definition];
      bySection.set(section, created);
      groups.push({ section, fields: created });
      continue;
    }
    existing.push(definition);
  }
  return groups;
}

/** A field's label in the reader's language. */
export function labelOf(definition: Pick<FieldDefinition, 'label'>, locale: Locale): string {
  return definition.label[locale];
}

/** A field's help text in the reader's language, or `null` when it has none. */
export function helpOf(definition: Pick<FieldDefinition, 'help'>, locale: Locale): string | null {
  const help = definition.help?.[locale] ?? '';
  return help.length === 0 ? null : help;
}

/** The chosen values a multiselect holds, whatever shape the control happened to be handed. */
function selectionOf(value: FormValue): readonly string[] {
  return typeof value === 'string' || typeof value === 'boolean' ? NO_SELECTION : value;
}

/** One field's value, coerced to the type its schema expects. */
function coerce(definition: FieldDefinition, value: FormValue): unknown {
  if (definition.type === 'number') {
    return Number(typeof value === 'string' ? value.trim() : value);
  }
  if (definition.type === 'multiselect') {
    return [...selectionOf(value)];
  }
  return typeof value === 'string' ? value.trim() : value;
}

/**
 * The field map to submit: every non-blank value, coerced, with every blank one omitted.
 *
 * A `required` field left blank is omitted too rather than sent as an empty string, so the server
 * refuses it with `errors.validation.required` naming the field — which is the message the operator
 * needs — instead of with a type error about an empty string.
 */
export function submittableFields(
  definitions: readonly FieldDefinition[],
  values: FormValues,
): Record<string, unknown> {
  const submitted: Record<string, unknown> = {};
  for (const definition of activeFields(definitions)) {
    const held = values[definition.key];
    if (held === undefined || isBlank(held)) {
      continue;
    }
    submitted[definition.key] = coerce(definition, held);
  }
  return submitted;
}

/**
 * The interpolation values a field's own definition can supply for its refusal message.
 *
 * Several validation messages carry a placeholder — `{{maxLength}}`, `{{min}}`, `{{max}}` — and a
 * message rendered without one shows literal braces to an operator, which is the defect
 * `errors.auth.account_locked` has on the authentication side and is worked around there by falling
 * back to the server's own rendering. Here there is no server rendering to fall back to, because the
 * refusal was decided in this process; the numbers are in the definition, so they are supplied.
 */
function messageParamsFor(definition: FieldDefinition): MessageParams {
  const { validation } = definition;
  switch (validation.type) {
    case 'text':
    case 'passport': {
      return {
        ...(validation.minLength === undefined ? {} : { minLength: validation.minLength }),
        ...(validation.maxLength === undefined ? {} : { maxLength: validation.maxLength }),
      };
    }
    case 'textarea': {
      return validation.maxLength === undefined ? {} : { maxLength: validation.maxLength };
    }
    case 'number': {
      return {
        ...(validation.min === undefined ? {} : { min: validation.min }),
        ...(validation.max === undefined ? {} : { max: validation.max }),
      };
    }
    case 'multiselect': {
      return {
        ...(validation.minSelected === undefined ? {} : { min: validation.minSelected }),
        ...(validation.maxSelected === undefined ? {} : { max: validation.maxSelected }),
      };
    }
    // The five types whose refusals carry no number of their own. Named rather than left to a
    // `default`, so a twelfth type added to the contract stops this compiling instead of quietly
    // rendering a message with an unfilled placeholder in it.
    case 'boolean':
    case 'select':
    case 'phone':
    case 'email':
    case 'country':
    case 'date': {
      return {};
    }
  }
}

/** A refusal, already rendered in the operator's language. */
export type FieldProblem = Readonly<Record<string, string>>;

/** The generic refusal, for a message the compiled validator produced that is not a catalogue key. */
const FALLBACK_KEY: MessageKey = 'errors.validation.invalid_format';

/**
 * One field's refusal, or `null`.
 *
 * **A blank `required` field IS a refusal, and a blank anything else is not.** The two are different
 * questions and this product answers them differently: `warn` is the completeness dialog, which is a
 * prompt an operator confirms past, while `required` is a write the server refuses outright. Leaving
 * the second to the server meant a mandatory field produced a round trip and a banner rather than a
 * marked control — which is exactly how an operator ends up rereading a form to find what is wrong.
 *
 * It does not turn a form red the moment it opens: `person-form.tsx` renders a problem only after
 * the first Save, and Save is never disabled by one, so the refusal arrives when the operator asks
 * for it rather than while they are still typing.
 */
export function validateField(
  definition: FieldDefinition,
  value: FormValue,
  context: { readonly today: string; readonly locale: Locale },
): string | null {
  if (isBlank(value)) {
    return definition.requirement === 'required'
      ? translate(context.locale, 'errors.validation.required')
      : null;
  }
  const parsed = fieldValueSchema(definition, { today: context.today }).safeParse(
    coerce(definition, value),
  );
  if (parsed.success) {
    return null;
  }
  const issue = parsed.error.issues[0];
  if (issue === undefined) {
    return translate(context.locale, FALLBACK_KEY);
  }
  // **The same mapper the server runs**, and that is the point of it being in the contracts package:
  // this side and the request edge compile the SAME schema from the SAME registry, so a refusal an
  // operator reads before they press Save and the one they read after have to be one sentence. This
  // file used to read `issue.message` and take it only when it happened to be a catalogue key, which
  // meant that the day the schemas stopped declaring a message on every bound — because the issue's
  // own origin says it better — every one of those refusals here silently became "the format is not
  // valid".
  const key = messageKeyOfIssue(issue, 'machine-key');
  // The definition's own bounds sit UNDER the issue's: both describe the same rule, and the issue is
  // the one that knows which half of it failed.
  return translate(context.locale, key, {
    ...messageParamsFor(definition),
    ...paramsOfIssue(key, issue, 'machine-key'),
  });
}

/**
 * Every refusal in the form, keyed by field, including the cross-field rules.
 *
 * The cross-field pass runs over the SUBMITTABLE map rather than over the raw controls, because
 * "a passport that expires before its holder was born" is a statement about two stored values and an
 * empty control holds neither.
 */
export function validateAll(
  definitions: readonly FieldDefinition[],
  values: FormValues,
  context: { readonly today: string; readonly locale: Locale },
): FieldProblem {
  const problems: Record<string, string> = {};
  for (const definition of activeFields(definitions)) {
    const held = values[definition.key] ?? blankValueFor(definition);
    const problem = validateField(definition, held, context);
    if (problem !== null) {
      problems[definition.key] = problem;
    }
  }
  for (const crossField of checkCrossFieldRules(submittableFields(definitions, values))) {
    const key = isMessageKey(crossField.messageKey) ? crossField.messageKey : FALLBACK_KEY;
    problems[crossField.fieldKey] = translate(context.locale, key);
  }
  return problems;
}

/** One operator-added field: a name and a value, for this record only. */
export interface AdhocDraft {
  readonly name: string;
  readonly value: string;
}

/**
 * The ad-hoc rows to submit: the ones that name something, trimmed.
 *
 * A row with a name and no value is kept, because an operator who typed a label and has not yet
 * been handed the value is recording a real intention; a row with neither is a blank line they
 * added and did not use.
 */
export function submittableAdhoc(drafts: readonly AdhocDraft[]): AdhocDraft[] {
  return drafts
    .map((draft) => ({ name: draft.name.trim(), value: draft.value.trim() }))
    .filter((draft) => draft.name.length > 0);
}

/**
 * Why a set of ad-hoc rows cannot be submitted, or `null`.
 *
 * Both rules are `adhocFieldsSchema`'s on the server, checked here so the eleventh row is refused in
 * the interface as well as by the API: an operator who fills in a row and is told at submission time
 * that it was never going to be accepted has lost the work, and the explanation arrives too late to
 * act on.
 *
 * **It reports the finding rather than a sentence.** The server's own message for a duplicate value
 * interpolates the field's KEY, and an ad-hoc field's name is prose an operator typed; rendering it
 * back through the error catalogue would put that prose into a message written for machine field
 * keys. The two sentences this needs are in the browser's own catalogue, where prose belongs.
 *
 * **The duplicate test is EXACT, because `adhocFieldsSchema`'s is.** Folding case here — which this
 * did at first — refuses `Talla` beside `talla`, which the API accepts; that is a refusal the API
 * would not make, which is the one thing this module says it never does. Two names that differ only
 * in case are a near-duplicate rather than a duplicate, and the autocomplete in `adhoc-names.ts` is
 * what keeps them from arising: it folds when it REMEMBERS a label, where folding costs nobody a
 * registration.
 */
export type AdhocProblem =
  { readonly kind: 'limit'; readonly limit: number } | { readonly kind: 'duplicate_name' };

export function adhocProblem(drafts: readonly AdhocDraft[]): AdhocProblem | null {
  const submittable = submittableAdhoc(drafts);
  if (submittable.length > MAX_ADHOC_FIELDS_PER_PERSON) {
    return { kind: 'limit', limit: MAX_ADHOC_FIELDS_PER_PERSON };
  }
  const names = submittable.map((draft) => draft.name);
  return new Set(names).size === names.length ? null : { kind: 'duplicate_name' };
}

/**
 * The form's values as the flat string map the conflict dialog compares (AD-15).
 *
 * **A conflict is answered per FIELD, so both sides have to be the same kind of thing.** The dialog
 * takes `Record<string, string | null>`, and what it shows is what an operator reads — so a boolean
 * travels as the token it is stored under and a multiselect as its values joined by a comma, which
 * is lossless because a machine key can contain neither a comma nor a space.
 *
 * A blank is `null` rather than `''`, because the dialog renders `null` as "(no value)" and an empty
 * string as a value that happens to be empty; a field one side cleared is a real divergence and has
 * to read as one.
 */
export function toFieldMap(
  definitions: readonly FieldDefinition[],
  values: FormValues,
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  for (const definition of activeFields(definitions)) {
    const held = values[definition.key] ?? blankValueFor(definition);
    map[definition.key] = isBlank(held) ? null : encodeValue(held);
  }
  return map;
}

/** One held value as the string the conflict dialog compares and shows. */
function encodeValue(value: FormValue): string {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return typeof value === 'string' ? value.trim() : value.join(',');
}

/** A stored multiselect, whether it arrived as an array from the API or as the encoded string. */
function storedSelection(stored: unknown): readonly string[] {
  if (Array.isArray(stored)) {
    return (stored as readonly unknown[]).filter(
      (entry): entry is string => typeof entry === 'string',
    );
  }
  return typeof stored === 'string' && stored.length > 0 ? stored.split(',') : NO_SELECTION;
}

/** How each type reads a stored value back into the shape its control holds. */
const DECODERS: Readonly<Record<FieldType, (stored: unknown) => FormValue>> = {
  boolean: (stored) => stored === true || stored === 'true',
  multiselect: storedSelection,
  text: asControlText,
  textarea: asControlText,
  number: asControlText,
  date: asControlText,
  select: asControlText,
  phone: asControlText,
  email: asControlText,
  country: asControlText,
  passport: asControlText,
};

/**
 * A stored value as the text a control shows.
 *
 * Anything that is neither a string nor a number becomes the empty string rather than
 * `String(...)` of whatever it was: a record that somehow holds an object for a text field should
 * render as empty and be re-entered, not as `[object Object]` in front of an operator.
 */
function asControlText(stored: unknown): FormValue {
  if (typeof stored === 'string') {
    return stored;
  }
  return typeof stored === 'number' ? String(stored) : '';
}

/** A stored value from a record, in the shape its control holds. */
export function decodeValue(definition: FieldDefinition, stored: unknown): FormValue {
  if (stored === null || stored === undefined) {
    return blankValueFor(definition);
  }
  return DECODERS[definition.type](stored);
}

/** A record's stored field map as the form's controls hold it. */
export function valuesFromRecord(
  definitions: readonly FieldDefinition[],
  stored: Readonly<Record<string, unknown>>,
): FormValues {
  const values: Record<string, FormValue> = {};
  for (const definition of activeFields(definitions)) {
    values[definition.key] = decodeValue(definition, stored[definition.key]);
  }
  return values;
}

/** The merge the conflict dialog produced, back in the shape the controls hold. */
export function valuesFromFieldMap(
  definitions: readonly FieldDefinition[],
  map: Readonly<Record<string, string | null>>,
): FormValues {
  const values: Record<string, FormValue> = {};
  for (const definition of activeFields(definitions)) {
    const held = map[definition.key];
    values[definition.key] =
      held === null || held === undefined
        ? blankValueFor(definition)
        : decodeValue(definition, held);
  }
  return values;
}
